/** @format */

import { CloseOutlined, FileTextOutlined, SearchOutlined } from "@ant-design/icons";
import { Button, Checkbox, Input, Select } from "antd";
import moment from "moment";
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import styled from "styled-components";
import { TLocation } from "../../../../redux-modules/declarations/maps/locations";
import { TShiftType } from "../../../../redux-modules/declarations/maps/shiftTypes";
import { TPersonaSettimana } from "../../../../redux-modules/declarations/maps/turniSettimana";
import { formatOreDaMinuti, nomeCompleto, normalizza } from "../format";
import { C } from "../styles";

const LARGHEZZA = 340;
const MARGINE = 12;

export const minutiTipo = (tipo: TShiftType): number => parseInt(tipo.working_minutes, 10) || 0;

export const orarioTipo = (tipo: TShiftType): string =>
  `${(tipo.time_from || "").substr(0, 5)}\u2013${(tipo.time_to || "").substr(0, 5)}`;

const eRiposo = (tipo: TShiftType): boolean => minutiTipo(tipo) === 0 && /riposo/i.test(tipo.name);

interface Props {
  persona: TPersonaSettimana;
  data: string;
  ancora: DOMRect;
  tipiTurno: TShiftType[];
  sedi: TLocation[];
  onAssegna: (
    tipoTurnoId: string,
    nascondiFine: boolean,
    sedeId: number,
    nota: string,
  ) => Promise<boolean>;
  onChiudi: () => void;
}

/**
 * Assegnazione rapida (sostituisce la finestra "Nuovo turno"): orari già usati dalla persona,
 * ricerca su tutti i tipi turno e, al passaggio del mouse, l'effetto sulla settimana prima di salvare.
 * Il clic su un orario lo seleziona soltanto: l'assegnazione parte con il tasto "Conferma".
 */
export const AssegnazioneRapida = ({
  persona,
  data,
  ancora,
  tipiTurno,
  sedi,
  onAssegna,
  onChiudi,
}: Props): JSX.Element => {
  const pannello = useRef<HTMLDivElement>(null);
  const [posizione, setPosizione] = useState({ left: ancora.left, top: ancora.bottom + 6 });
  const [ricerca, setRicerca] = useState("");
  const [nascondiFine, setNascondiFine] = useState(false);
  const [sedeId, setSedeId] = useState<number>(persona.sede_id);
  const [evidenziato, setEvidenziato] = useState<TShiftType | null>(null);
  const [selezionato, setSelezionato] = useState<TShiftType | null>(null);
  const [salvataggio, setSalvataggio] = useState(false);
  // la nota è facoltativa: il campo si apre solo se serve, per non allungare il pannello
  const [conNota, setConNota] = useState(false);
  const [nota, setNota] = useState("");

  const giorno = persona.giorni.find(g => g.data === data);

  useLayoutEffect(() => {
    const altezza = pannello.current ? pannello.current.offsetHeight : 460;
    const left = Math.max(MARGINE, Math.min(ancora.left, window.innerWidth - LARGHEZZA - MARGINE));
    let top = ancora.bottom + 6;
    if (top + altezza > window.innerHeight - MARGINE) {
      top = Math.max(MARGINE, ancora.top - altezza - 6);
    }
    setPosizione({ left, top });
  }, [ancora]);

  useEffect(() => {
    const tasto = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onChiudi();
    };
    document.addEventListener("keydown", tasto);
    return (): void => document.removeEventListener("keydown", tasto);
  }, [onChiudi]);

  const usati = useMemo((): TShiftType[] => {
    const ids: string[] = [];
    persona.giorni.forEach(g =>
      g.turni.forEach(t => {
        const id = String(t.tipo_turno_id);
        if (t.lavorativo && ids.indexOf(id) < 0) ids.push(id);
      }),
    );
    const trovati = ids
      .map(id => tipiTurno.find(t => String(t.id) === id))
      .filter((t): t is TShiftType => !!t)
      .slice(0, 4);
    const riposo = tipiTurno.find(eRiposo);
    return riposo ? [...trovati, riposo] : trovati;
  }, [persona, tipiTurno]);

  const elenco = useMemo((): TShiftType[] => {
    const testo = normalizza(ricerca);
    if (!testo) return tipiTurno;
    return tipiTurno.filter(
      t => normalizza(t.name).indexOf(testo) >= 0 || orarioTipo(t).indexOf(testo) >= 0,
    );
  }, [ricerca, tipiTurno]);

  const scegli = (tipo: TShiftType): void => {
    if (salvataggio) return;
    setSelezionato(tipo);
  };

  const conferma = (): void => {
    if (salvataggio || !selezionato) return;
    setSalvataggio(true);
    onAssegna(String(selezionato.id), nascondiFine, sedeId, conNota ? nota : "").then(ok => {
      if (ok) onChiudi();
      else setSalvataggio(false);
    });
  };

  const eSelezionato = (tipo: TShiftType): boolean =>
    !!selezionato && String(selezionato.id) === String(tipo.id);

  const totali = persona.totali;
  let impatto = `Settimana: ${formatOreDaMinuti(
    totali.minuti_assegnati,
  )} h assegnate di ${formatOreDaMinuti(totali.minuti_previsti)} previste`;
  let oltre = false;
  // anteprima: l'orario sotto il mouse, altrimenti quello selezionato
  const anteprima = evidenziato || selezionato;
  if (anteprima && minutiTipo(anteprima) > 0) {
    const dopo = totali.minuti_assegnati + minutiTipo(anteprima);
    oltre = dopo > totali.minuti_contratto;
    impatto = `Con questo turno: ${formatOreDaMinuti(dopo)} h di ${formatOreDaMinuti(
      totali.minuti_previsti,
    )} previste${oltre ? ", oltre il contratto" : ""}`;
  }

  const bottone = (tipo: TShiftType, classe: string): JSX.Element => (
    <button
      key={`${classe}-${tipo.id}`}
      type="button"
      className={eSelezionato(tipo) ? `${classe} selezionato` : classe}
      aria-pressed={eSelezionato(tipo)}
      disabled={salvataggio}
      onClick={(): void => scegli(tipo)}
      onMouseEnter={(): void => setEvidenziato(tipo)}
      onMouseLeave={(): void => setEvidenziato(null)}
      onFocus={(): void => setEvidenziato(tipo)}
      onBlur={(): void => setEvidenziato(null)}
    >
      {classe === "chip" ? (
        tipo.name
      ) : (
        <>
          <span className="nome-tipo">{tipo.name}</span>
          <span className="durata">
            {minutiTipo(tipo) > 0
              ? `${orarioTipo(tipo)}, ${formatOreDaMinuti(minutiTipo(tipo))} h`
              : "nessuna ora"}
          </span>
        </>
      )}
    </button>
  );

  return ReactDOM.createPortal(
    <>
      <Sfondo type="button" aria-label="Chiudi" onClick={onChiudi} />
      <Pannello
        ref={pannello}
        role="dialog"
        aria-label="Assegna turno"
        style={{ left: posizione.left, top: posizione.top }}
      >
        <div className="testa">
          <div>
            <div className="titolo">Assegna turno</div>
            <div className="sottotitolo">
              {nomeCompleto(persona)}, {moment(data).format("dddd D MMMM")}
            </div>
          </div>
          <button type="button" className="chiudi" aria-label="Chiudi" onClick={onChiudi}>
            <CloseOutlined />
          </button>
        </div>

        {giorno && !giorno.previsto && (
          <div className="avviso">
            Il contratto non prevede questo giorno: puoi assegnare il turno, comparirà un avviso.
          </div>
        )}
        {giorno?.festivita && (
          <div className="avviso">Festività della sede: {giorno.festivita}</div>
        )}

        {usati.length > 0 && (
          <>
            <div className="etichetta">Già usati da questa persona</div>
            <div className="chips">{usati.map(t => bottone(t, "chip"))}</div>
          </>
        )}

        <Input
          autoFocus
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Cerca un orario, es. 14:30"
          aria-label="Cerca un orario"
          value={ricerca}
          onChange={(e): void => setRicerca(e.target.value)}
        />
        <div className="elenco">
          {elenco.map(t => bottone(t, "voce"))}
          {elenco.length === 0 && <div className="nessuno">Nessun turno con questo orario.</div>}
        </div>

        <div className="opzioni">
          <Checkbox
            checked={nascondiFine}
            onChange={(e): void => setNascondiFine(e.target.checked)}
          >
            Nascondi fine turno
          </Checkbox>
          <label className="sede">
            Sede
            <Select
              size="small"
              value={sedeId}
              onChange={(v: number): void => setSedeId(v)}
              style={{ minWidth: 140 }}
              getPopupContainer={(): HTMLElement => pannello.current || document.body}
            >
              {sedi.map(s => (
                <Select.Option key={s.id} value={Number(s.id)}>
                  {Number(s.id) === persona.sede_id ? `${s.name} (abituale)` : s.name}
                </Select.Option>
              ))}
            </Select>
          </label>
        </div>

        {conNota ? (
          <Input.TextArea
            autoFocus
            className="nota"
            aria-label="Nota del turno"
            placeholder="Nota del turno (facoltativa)"
            value={nota}
            maxLength={5000}
            autoSize={{ minRows: 2, maxRows: 4 }}
            onChange={(e): void => setNota(e.target.value)}
          />
        ) : (
          <button type="button" className="aggiungi-nota" onClick={(): void => setConNota(true)}>
            <FileTextOutlined /> Aggiungi una nota
          </button>
        )}

        <div className={`impatto${oltre ? " oltre" : ""}`} aria-live="polite">
          {impatto}
        </div>

        <div className="piede">
          <span className="scelta" title={selezionato ? selezionato.name : undefined}>
            {selezionato ? selezionato.name : "Scegli orario"}
          </span>
          <div className="azioni">
            <Button onClick={onChiudi} disabled={salvataggio}>
              Annulla
            </Button>
            <Button
              type="primary"
              onClick={conferma}
              disabled={!selezionato}
              loading={salvataggio}
            >
              Conferma
            </Button>
          </div>
        </div>
      </Pannello>
    </>,
    document.body,
  );
};

const Sfondo = styled.button`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1040;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: default;
`;

const Pannello = styled.div`
  position: fixed;
  z-index: 1041;
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: ${LARGHEZZA}px;
  max-width: calc(100vw - ${MARGINE * 2}px);
  padding: 14px;
  background: #fff;
  border: 1px solid #dadde8;
  border-radius: 12px;
  box-shadow: 0 14px 36px rgba(30, 35, 51, 0.2);
  color: ${C.testo};

  .testa {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 8px;
  }
  .titolo {
    font-size: 15px;
    font-weight: 700;
  }
  .sottotitolo {
    font-size: 13px;
    font-weight: 600;
    color: ${C.testoSecondario};
  }
  .chiudi {
    display: grid;
    place-items: center;
    width: 32px;
    height: 32px;
    border: 0;
    border-radius: 8px;
    background: transparent;
    cursor: pointer;
  }
  .avviso {
    padding: 8px 10px;
    border-radius: 8px;
    font-size: 12.5px;
    font-weight: 600;
    background: ${C.avviso.fondo};
    color: #6e4300;
  }
  .etichetta {
    font-size: 12px;
    font-weight: 700;
    color: ${C.testoSecondario};
  }
  .chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }
  .chip {
    height: 32px;
    padding: 0 10px;
    border: 1px solid ${C.daSvolgere.bordo};
    border-radius: 8px;
    background: ${C.daSvolgere.fondo};
    color: ${C.daSvolgere.testo};
    font: inherit;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
  }
  .chip:hover,
  .chip:focus-visible {
    border-color: ${C.daSvolgere.hover};
  }
  .chip.selezionato {
    border-color: ${C.daSvolgere.testo};
    background: ${C.daSvolgere.testo};
    color: #fff;
  }
  .elenco {
    display: flex;
    flex-direction: column;
    max-height: 180px;
    overflow: auto;
    border: 1px solid ${C.lineaLeggera};
    border-radius: 8px;
  }
  .voce {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    padding: 8px 10px;
    border: 0;
    border-bottom: 1px solid #f0f1f5;
    background: #fff;
    font: inherit;
    text-align: left;
    cursor: pointer;
  }
  .voce:hover,
  .voce:focus-visible {
    background: #f4f5fc;
  }
  .voce.selezionato {
    background: ${C.daSvolgere.fondo};
    box-shadow: inset 3px 0 0 ${C.daSvolgere.testo};
  }
  .voce.selezionato .nome-tipo {
    color: ${C.daSvolgere.testo};
  }
  .nome-tipo {
    font-size: 13.5px;
    font-weight: 700;
  }
  .durata {
    flex-shrink: 0;
    font-size: 12px;
    color: ${C.testoSecondario};
  }
  .nessuno {
    padding: 10px;
    font-size: 12.5px;
    color: ${C.testoSecondario};
  }
  .opzioni {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px 16px;
    font-size: 13px;
  }
  .sede {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-weight: 600;
  }
  .aggiungi-nota {
    align-self: flex-start;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 0;
    border: 0;
    background: transparent;
    font: inherit;
    font-size: 13px;
    font-weight: 600;
    color: ${C.daSvolgere.testo};
    cursor: pointer;
  }
  .aggiungi-nota:hover,
  .aggiungi-nota:focus-visible {
    text-decoration: underline;
  }
  .nota {
    font-size: 13px;
  }
  .impatto {
    min-height: 34px;
    padding: 8px 10px;
    border-radius: 8px;
    font-size: 12.5px;
    font-weight: 700;
    background: #f3f4f8;
    color: #3a4160;
  }
  .impatto.oltre {
    background: ${C.avviso.fondo};
    color: #6e4300;
  }
  .piede {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    padding-top: 10px;
    border-top: 1px solid ${C.lineaLeggera};
  }
  .scelta {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 13px;
    font-weight: 700;
    color: ${C.testoSecondario};
  }
  .azioni {
    display: flex;
    flex-shrink: 0;
    gap: 8px;
  }
`;